import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Grid2 as Grid,
  CircularProgress,
  styled
} from '@mui/material'
import { getPatientCalculations } from 'services/patient'
import {
  IPatientCalculationsResponse,
  IPatientVitalSignsResponse
} from 'types/patient/types'
import dayjs from 'dayjs'
import HealthCard, { IHealthCardProps } from '../../../../components/HealthCard'
import HealthSectionSkeleton from './skeleton'
import { NavLink } from 'react-router-dom'
import { PATIENT_HEALTH_DATA_DETAIL } from 'navigation/CONSTANTS'
import { decimalToString } from 'utils/decimal'
import SepsesRiskBanner from './sepsesRiskBanner'
import HDABanner from './hdaBanner'
import { useShowBannerHDA } from 'utils/hda'
import OutdatedAlert from './outdatedAlert'

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none'
}))

const StyledOverallHealthBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '240px',
  cursor: 'pointer',
  color: theme.palette.text.primary,
  '&:hover': {
    transform: 'scale(1.01)'
  }
}))

const CircularProgressWithLabel = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center'
}))

const CircularProgressText = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  fontWeight: 700,
  fontSize: '1.5rem'
}))

interface IPatientCalculationSectionProps {
  patientId: string
  vitalSigns?: IPatientVitalSignsResponse
}

const PatientCalculationSection: React.FC<IPatientCalculationSectionProps> = ({
  patientId,
  vitalSigns
}) => {
  const [state, setState] = useState<IStateStdTypes>('idle')
  const [patientCalculations, setPatientCalculations] =
    useState<IPatientCalculationsResponse>()

  const showBannerHda = useShowBannerHDA()

  const fetchCalculations = async () => {
    const response = await getPatientCalculations(patientId)
    if (response.success) {
      setPatientCalculations(response.data)
      setState('success')
    } else {
      setState('error')
    }
  }

  useEffect(() => {
    let interval: NodeJS.Timer
    if (patientId) {
      setState('loading')
      fetchCalculations()
      interval = setInterval(() => {
        fetchCalculations()
      }, 60000 * 2)
    }

    return () => {
      clearInterval(interval)
    }
  }, [])

  if (state === 'loading') {
    return <HealthSectionSkeleton />
  }

  if (!patientCalculations) {
    // TODO: create default component
    return <div>Erro ao buscar dados</div>
  }

  const healthData: IHealthCardProps[] = [
    {
      icon: <span className='material-symbols-outlined'>cognition_2</span>,
      title: 'Risco de AVC',
      data: 'stroke_calculation',
      values: [
        {
          type: '%',
          value: patientCalculations.stroke_calculation
        }
      ],
      timestamp: patientCalculations.stroke_calculation_timestamp,
      hide: showBannerHda
    },
    {
      icon: <span className='material-symbols-outlined'>heart_broken</span>,
      title: 'Risco de Doença Cardiovascular',
      data: 'cvd_calculation',
      values: [
        {
          type: '%',
          value: patientCalculations.cvd_calculation
        }
      ],
      timestamp: patientCalculations.cvd_calculation_timestamp,
      hide: showBannerHda
    },
    {
      icon: <span className='material-symbols-outlined'>cardiology</span>,
      title: 'Risco de Hipertensão',
      data: 'hypertension_calculation',
      values: [
        {
          type: '%',
          value: patientCalculations.hypertension_calculation
        }
      ],
      timestamp: patientCalculations.hypertension_calculation_timestamp,
      hide: showBannerHda
    },
    {
      icon: <span className='material-symbols-outlined'>pulse_alert</span>,
      title: 'Risco de Fibrilação Atrial',
      data: 'atrial_fibrillation_calculation',
      values: [
        {
          type: '%',
          value: patientCalculations.atrial_fibrillation_calculation
        }
      ],
      timestamp: patientCalculations.atrial_fibrillation_calculation_timestamp,
      hide: showBannerHda
    },
    {
      icon: <span className='material-symbols-outlined'>sleep_score</span>,
      title: 'Pontuação de Sono',
      data: 'sleep_score',
      values: [
        {
          type: '',
          value: patientCalculations.sleep_score
        }
      ],
      timestamp: patientCalculations.sleep_score_timestamp
    },
    {
      icon: <span className='material-symbols-outlined'>readiness_score</span>,
      title: 'Estressores e Sobrecarga Fisiológica',
      data: 'stress_level_calculation',
      values: [
        {
          type: '',
          value: patientCalculations.stress_level_calculation
        }
      ],
      timestamp: patientCalculations.stress_level_calculation_timestamp
    },
    {
      icon: <span className='material-symbols-outlined'>heart_plus</span>,
      title: 'Idade Cardíaca/Vascular',
      data: 'heart_age_calculation',
      values: [
        {
          type: '',
          value: patientCalculations.heart_age_calculation
        }
      ],
      timestamp: patientCalculations.heart_age_calculation_timestamp,
      hide: showBannerHda
    },
    {
      icon: <span className='material-symbols-outlined'>body_fat</span>,
      title: 'Índice de Massa Corporal (IMC)',
      data: 'bmi',
      values: [
        {
          type: '',
          value: patientCalculations.bmi
            ? decimalToString(Number.parseFloat(patientCalculations.bmi))
            : null
        }
      ],
      timestamp: patientCalculations.bmi_timestamp
    }
  ]

  const overall = patientCalculations.overall_calculation ?? 100

  return (
    <Box sx={{ padding: 2 }} flex={1}>
      <Grid
        sx={{
          marginBottom: '12px'
        }}
      >
        <OutdatedAlert
          vitalSigns={vitalSigns ?? ({} as IPatientVitalSignsResponse)}
          isHDA={showBannerHda}
        />
      </Grid>
      {showBannerHda ? (
        <HDABanner
          vitalSigns={vitalSigns ?? ({} as IPatientVitalSignsResponse)}
        />
      ) : (
        <SepsesRiskBanner
          overallHealth={overall}
          overallHealthColor={patientCalculations.overall_calculation_color}
          overallHealthWarning={
            patientCalculations.overall_calculation_warning ?? 0
          }
        />
      )}
      <Typography variant='h6' fontWeight='bold' gutterBottom>
        Suporte ao médico:
      </Typography>
      <Grid container spacing={2}>
        {!showBannerHda && (
          <Grid
            size={{
              md: 3,
              xs: 12
            }}
          >
            <StyledNavLink
              to={`/${PATIENT_HEALTH_DATA_DETAIL(patientId, 'overall_calculation')}`}
            >
              <StyledOverallHealthBox>
                <CircularProgressWithLabel>
                  <CircularProgress
                    variant='determinate'
                    value={patientCalculations.overall_calculation ?? 100}
                    size={150}
                    thickness={5}
                    sx={{
                      color: patientCalculations.overall_calculation
                        ? `rgba(${patientCalculations.overall_calculation_color})`
                        : 'grey',
                      [`& .${'MuiCircularProgress-circle'}`]: {
                        strokeLinecap: 'round'
                      }
                    }}
                  />
                  <CircularProgressText>
                    {patientCalculations.overall_calculation ? (
                      `${patientCalculations.overall_calculation}%`
                    ) : (
                      <Typography variant='caption' textAlign='center'>
                        Sem novos dados
                      </Typography>
                    )}
                  </CircularProgressText>
                </CircularProgressWithLabel>
              </StyledOverallHealthBox>
            </StyledNavLink>
            <Typography align='center'>Pontuação Geral da Saúde</Typography>
            {patientCalculations.overall_calculation ? (
              <Typography align='center' color='textSecondary'>
                {dayjs(
                  patientCalculations.overall_calculation_timestamp
                ).format('DD/MM HH:mm')}
              </Typography>
            ) : (
              <></>
            )}
          </Grid>
        )}
        <Grid
          size={{
            md: 9,
            xs: 12
          }}
        >
          <Grid container spacing={2}>
            {healthData
              .filter(item => !item.hide)
              .map((data, index) => (
                <Grid
                  key={index}
                  size={{
                    xs: 6,
                    md: 4
                  }}
                >
                  <HealthCard patientId={patientId} {...data} />
                </Grid>
              ))}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PatientCalculationSection
